<template>
  <list-layout
    :title="(!isSubTab ? $t('transactions') : '') | startcase"
    :search-filter="!payoutsTransactions ? filters.search: null"
    :apply-filters="onFiltersApply"
    no-counts
    no-apply-btn
    no-search
  >
    <modal
      id="transactions-export-modal"
      :customTitle="$t('export_transactions_list') | capitalize"
      modal-class="custom-modal edit"
      modalType="edit"
      size="xl"
      centered
      hide-footer>
        <transactions-export-form :count="count"/>
    </modal>
    <template v-slot:beforeFilters>
      <b-row v-if="!isSubTab">
        <b-col>
          <tabs-layout ref="transactionsTabs" :tabs="tabs" @change="onTabChange">
            <template v-slot:tabTitle="{ tab }">
              {{ tab.title }}
            </template>
          </tabs-layout>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <filter-builder
            v-if="currentTab === 'transactions_tab'"
            :id="filterBuilderId"
            ref="transactionFilters"
            :filters="activeFilters"
            :apply-filters="onFiltersApply"
            :reset-filters="resetFilters"
            :filters-pending="filtersPending"
            :filters-applied="filtersApplied"
            @filter="onFilter"
          />
        </b-col>
      </b-row>
      <b-row class="text-center mb-3" align-v="center">
        <b-col>
          <b-form-radio-group
            v-if="isSubTab && !payoutsTransactions && currentTab !== 'payouts_tab'"
            v-model="currentTab"
            :options="transactionsOptions"
            buttons
            button-variant="outline-secondary"
            name="radios-btn-outline"
            class="radio-buttons mr-3"
          />
          <b-form-radio-group
            v-if="firstLoaded && currentTab === 'transactions_tab'"
            v-model="transactionsTable.selectedAggregation"
            :options="transactionsTable.aggregationOptions"
            buttons
            button-variant="outline-secondary"
            name="radios-btn-outline"
            class="radio-buttons"
          />
        </b-col>
      </b-row>
    </template>
    <template v-slot:table>
      <div
        v-if="!firstLoaded && currentTab === 'transactions_tab'"
        class="w-100 d-flex flex-column justify-content-center align-items-center flex-fill"
      >
        <div class="mt-3 mb-3">
          <b-img :src="require('@/assets/no_filter.png')" class="no-filter-img"/>
        </div>
        <div class="no-filters-text">
          {{ $t('transactions_select_filter_text') }}
        </div>
        <div>
          <b-button class="btn-secondary" @click="loadAllTransactions">
            {{ $t('load_all_transactions') }}
          </b-button>
        </div>
      </div>
      <t-table
        v-if="firstLoaded && currentTab === 'transactions_tab'"
        :columns="transactionColumns"
        :options="transactionTableOptions"
        :limit="limit"
        :id="transactionTableId"
        :key="transactionTableId"
        ref="transactions"
        @loaded="onLoaded"
        @loading="onLoading"
        @pagination="onPagination"
        @sorted="onSorted"
      >
        <!-- Type -->
        <template v-slot:type="{ row }">
          {{ $t(row.type) | capitalize }}
        </template>
        <!-- Performer Category -->
        <template v-slot:performerCategory="{ row }">
          {{ row.performerCategory ? $t(transactionPerformerCategories[row.performerCategory]) : "" | capitalize }}
        </template>
        <template v-slot:merge>
          <span v-if="isSubTab && !payoutsTransactions">
            <b-button size="sm"
              class="ml-2 btn-outline-red"
              :to="{
                name: 'transactions-list',
                query: {
                  recordingProprietaryId: recordingResoundId,
                  rightsHolderProprietaryId: rightsholderResoundId,
                  distribution_id: distributionId,
                  distribution: distributionValue,
                  },
              }">
              {{ $t("view_transactions") | capitalize }}
            </b-button>
          </span>
          <span>
            <b-button
              :disabled="!canExport"
              size="sm"
              @click="exportData"
              class="ml-2 btn-outline-red"
            >
              <fa-icon size="sm" :icon="['fa', 'cloud-upload-alt']" />
              {{ $t("export") | capitalize }}
            </b-button>
          </span>
        </template>
        <template v-slot:counts>
          <div class="d-flex">
            <pagination
              class="smaller p-0"
              :page="page"
              :count="count"
              :loading="loading"
              v-model="limit"
              @limit="onLimit"
            />
          </div>
        </template>

        <!-- Yes / No fields -->

        <template v-slot:payable="{ row }">
          <template v-if="row.payable">
            <fa-icon size="sm" :icon="['fas', 'check']" class="text-green" />
          </template>
          <template v-else>
            <fa-icon size="sm" :icon="['fas', 'times']" class="text-red" />
          </template>
        </template>

        <template v-slot:preview="{ row }">
          <template v-if="row.preview">
            <fa-icon size="sm" :icon="['fas', 'check']" class="text-green" />
          </template>
          <template v-else>
            <fa-icon size="sm" :icon="['fas', 'times']" class="text-red" />
          </template>
        </template>

        <template v-slot:hold="{ row }">
          <template v-if="row.hold">
            <fa-icon size="sm" :icon="['fas', 'times']" class="text-red" />
          </template>
          <template v-else>
            <fa-icon size="sm" :icon="['fas', 'check']" class="text-green" />
          </template>
        </template>

        <template v-slot:mandateConflict="{ row }">
          <template v-if="row.mandateConflict">
            <fa-icon size="sm" :icon="['fas', 'times']" class="text-red" />
          </template>
          <template v-else>
            <fa-icon size="sm" :icon="['fas', 'check']" class="text-green" />
          </template>
        </template>

        <template v-slot:ownershipConflict="{ row }">
          <template v-if="row.ownershipConflict">
            <fa-icon size="sm" :icon="['fas', 'times']" class="text-red" />
          </template>
          <template v-else>
            <fa-icon size="sm" :icon="['fas', 'check']" class="text-green" />
          </template>
        </template>

        <template v-slot:lineupConflict="{ row }">
          <template v-if="row.lineupConflict">
            <fa-icon size="sm" :icon="['fas', 'times']" class="text-red" />
          </template>
          <template v-else>
            <fa-icon size="sm" :icon="['fas', 'check']" class="text-green" />
          </template>
        </template>

        <template v-slot:genreConflict="{ row }">
          <template v-if="row.genreConflict">
            <fa-icon size="sm" :icon="['fas', 'times']" class="text-red" />
          </template>
          <template v-else>
            <fa-icon size="sm" :icon="['fas', 'check']" class="text-green" />
          </template>
        </template>

        <template v-slot:eligibility="{ row }">
          <template v-if="row.eligibility === 'eligible'">
            <fa-icon size="sm" :icon="['fas', 'check']" class="text-green" />
          </template>
          <template v-else>
            <fa-icon size="sm" :icon="['fas', 'times']" class="text-red" />
          </template>
        </template>

        <template v-slot:pendingMakerMatching="{ row }">
          <template v-if="row.pendingMakerMatching">
           <fa-icon size="sm" :icon="['fas', 'times']" class="text-red" />
          </template>
          <template v-else>
            <fa-icon size="sm" :icon="['fas', 'check']" class="text-green" />
          </template>
        </template>

        <template v-slot:pendingPerformerMatching="{ row }">
          <template v-if="row.pendingPerformerMatching">
            <fa-icon size="sm" :icon="['fas', 'times']" class="text-red" />
          </template>
          <template v-else>
            <fa-icon size="sm" :icon="['fas', 'check']" class="text-green" />
          </template>
        </template>

        <template v-slot:missingMandate="{ row }">
          <template v-if="!row.missingMandate">
            <fa-icon size="sm" :icon="['fas', 'check']" class="text-green" />
          </template>
          <template v-else>
            <fa-icon size="sm" :icon="['fas', 'times']" class="text-red" />
          </template>
        </template>

        <template v-slot:hasFeaturedCount="{ row }">
          <template v-if="row.hasFeaturedCount">
            <fa-icon size="sm" :icon="['fas', 'check']" class="text-green" />
          </template>
          <template v-else>
            <fa-icon size="sm" :icon="['fas', 'times']" class="text-red" />
          </template>
        </template>

        <template v-slot:hasNonFeaturedCount="{ row }">
          <template v-if="row.hasNonFeaturedCount">
            <fa-icon size="sm" :icon="['fas', 'check']" class="text-green" />
          </template>
          <template v-else>
            <fa-icon size="sm" :icon="['fas', 'times']" class="text-red" />
          </template>
        </template>

        <!--  Link value fields-->

        <template v-slot:distributionId="{ row }">
          <database-link
            :type="linkTypes.DISTRIBUTION"
            :item-id="row.distributionId"
          >
            {{ row.distributionTariffCode }}
            {{ row.distributionPeriodStart | truncate(7) }} - {{ row.distributionPeriodEnd | truncate(7) }}
          </database-link>
        </template>
        <template v-slot:rightsHolderProprietaryId="{ row }">
          <database-link
            :type="linkTypes.RIGHTS_HOLDER"
            :item-id="row.rightsHolder?.id"
          >
            {{ row.rightsHolderProprietaryId }}
          </database-link>
        </template>
        <template v-slot:name="{ row }">
          <database-link
            :type="linkTypes.RIGHTS_HOLDER"
            :item-id="row.rightsHolder?.id"
          >
            {{ row.name }}
          </database-link>
        </template>
        <template v-slot:recordingProprietaryId="{ row }">
          <database-link
            :type="linkTypes.RECORDING"
            :item-id="row.soundRecording?.id"
          >
            {{ row.recordingProprietaryId }}
          </database-link>
        </template>
        <template v-slot:title="{ row }" >
          <database-link :type="linkTypes.RECORDING" :item-id="row.soundRecording?.id">
            {{ row.title }}
          </database-link>
        </template>

        <template v-slot:payoutId="{ row }" >
          <database-link :type="linkTypes.PAYOUT" :item-id="row.payoutId">
            {{ row.payoutDisplayId }}
          </database-link>
        </template>

        <!-- Multiple value fields -->
        <template v-slot:localProprietaryIds="{ row }">
          {{ getProprietaryIds(row.identifiers).length ? getProprietaryIds(row.identifiers)[0].code : "" }}
          <fa-icon
            v-if="getProprietaryIds(row.identifiers).length"
            class="blue clickable ml-2"
            :icon="['fas', 'plus-circle']"
            @click="showModal(`${row.recordingProprietaryId}-${row.payeeId}-${row.rightsHolderProprietaryId}-${row.distributionId}-proprietary-ids`)"
          />
          <b-modal
            :id="`${row.recordingProprietaryId}-${row.payeeId}-${row.rightsHolderProprietaryId}-${row.distributionId}-proprietary-ids`"
            :ref="`${row.recordingProprietaryId}-${row.payeeId}-${row.rightsHolderProprietaryId}-${row.distributionId}-proprietary-ids`"
            modal-class="custom-modal info"
            size="xl"
            centered
            hide-footer
          >
            <template #modal-title>
              <h4 class="d-flex align-items-center mb-0">
                <fa-icon :icon="['fas', 'exclamation-circle']" class="icon-blue mr-2"/>
                {{ $t('proprietary_ids') }}
              </h4>
            </template>
            <t-table
                :columns="localProprietaryIdsColumns"
                :options="localProprietaryIdsOptions"
                id="local-proprietary-ids-table"
                :local-data="getProprietaryIds(row.identifiers)"
            >
              <template v-slot:id="{ row: proprietaryIds }">
                {{ proprietaryIds.code }}
              </template>

              <template v-slot:mc="{ row: proprietaryIds }">
                {{ proprietaryIds.type }}
              </template>
            </t-table>
            <b-row class="modal-footer float-right mt-4">
              <b-button class="btn-outline-red" @click="$bvModal.hide(`${row.recordingProprietaryId}-${row.payeeId}-${row.rightsHolderProprietaryId}-${row.distributionId}-proprietary-ids`)">
                {{ $t("ok") | capitalize }}
              </b-button>
            </b-row>
          </b-modal>
        </template>

        <!-- Number fields -->

        <template v-slot:totalPlaycount="{ row }">
          {{ row.deltaPlaycount | formatNumber(2) }}
        </template>

        <template v-slot:transactionsCount="{ row }">
          {{ row.transactionsCount | formatNumber }}
        </template>

        <!-- Money amount fields -->

        <template v-slot:totalAmount="{ row }">
          ${{ row.deltaTotalAmount | formatNumber(2, 2) }}
        </template>
        <template v-slot:payableAmount="{ row }">
          ${{ row.deltaPayableAmount | formatNumber(2, 2) }}
        </template>
        <template v-slot:vpp="{ row }">
          ${{ row.vpp | formatNumber(8) }}
        </template>
        <template v-slot:baseFee="{ row }">
          ${{ row.baseFee | formatNumber(2, 2) }}
        </template>
        <template v-slot:serviceFee="{ row }">
          ${{ row.serviceFee | formatNumber(2, 2) }}
        </template>
        <template v-slot:withholdingTax="{ row }">
          ${{ row.withholdingTax | formatNumber(2, 2) }}
        </template>

        <!--  Percent fields  -->

        <template v-slot:share="{ row }">
          {{ row.share | formatNumber(2) }}%
        </template>

        <!--  Datetime fields -->

        <template v-slot:calculationTimestamp="{ row }">
          {{ parseDate(row.calculationTimestamp, $config.DATETIME_FORMAT) }}
        </template>

        <!-- Action fields -->

        <template v-slot:removeTransaction="{ row }">
          <div class="d-flex" v-if="permissions.actions.canRemoveTransactions">
          <b-button class="ml-3 btn-outline-red" @click="handleRemoveTransaction(row)">
            {{ $t("remove") | capitalize }}
          </b-button>
          </div>
        </template>

        <template v-slot:refreshTransaction="{ row }">
          <div class="d-flex" v-if="permissions.actions.canRefreshTransactions">
            <b-button class="ml-3" variant="outline-primary" @click="handleRefreshTransaction(row)">
              {{ $t("refresh") | capitalize }}
            </b-button>
          </div>
        </template>

        <template v-slot:moratorium="{ row }">
          <template v-if="row.moratorium">
            <fa-icon size="sm" :icon="['fas', 'times']" class="text-red" />
          </template>
          <template v-else>
            <fa-icon size="sm" :icon="['fas', 'check']" class="text-green" />
          </template>
        </template>

        <template v-slot:drillDown="{ row }">
          <fa-icon
            v-if="row.aggregatedData"
            class="blue clickable ml-2"
            :icon="['fas', 'plus-circle']"
            @click="showModal(`${row.recordingProprietaryId}-${row.payeeId}-${row.rightsHolderProprietaryId}-${row.distributionId}`)"
          />
          <b-modal
            :id="`${row.recordingProprietaryId}-${row.payeeId}-${row.rightsHolderProprietaryId}-${row.distributionId}`"
            :ref="`${row.recordingProprietaryId}-${row.payeeId}-${row.rightsHolderProprietaryId}-${row.distributionId}`"
            modal-class="custom-modal info"
            size="xl"
            centered
            hide-footer
          >
            <template #modal-title>
              <h4 class="d-flex align-items-center mb-0">
                <fa-icon :icon="['fas', 'exclamation-circle']" class="icon-blue mr-2"/>
                {{ $t('consolidated_share_details') | startcase }}
              </h4>
            </template>
            <t-table
                :columns="drillDownTableColumns"
                :options="drillDownTableOptions"
                id="drill-down-transactions-table"
                :local-data="row.aggregatedData"
            >
              <template v-slot:totalAmount="{ row: aggregatedRow }">
                ${{ aggregatedRow.totalAmount | formatNumber(2, 2) }}
              </template>

              <template v-slot:payableAmount="{ row: aggregatedRow }">
                ${{ aggregatedRow.payableAmount | formatNumber(2, 2) }}
              </template>

              <template v-slot:rawAmount="{ row: aggregatedRow }">
                ${{ aggregatedRow.rawAmount | formatNumber(4, 4) }}
              </template>

              <template v-slot:totalPlaycount="{ row: aggregatedRow }">
                {{ aggregatedRow.totalPlaycount | formatNumber }}
              </template>

              <template v-slot:share="{ row: aggregatedRow }">
                {{ aggregatedRow.share | formatNumber }}%
              </template>

              <template v-slot:name>
                <database-link
                    :type="linkTypes.RIGHTS_HOLDER"
                    :item-id="row.rightsHolder?.id"
                >
                  {{ row.name }}
                </database-link>
              </template>

              <template v-slot:payable>
                <template v-if="row.payable">
                  <fa-icon size="sm" :icon="['fas', 'check']" class="text-green" />
                </template>
                <template v-else>
                  <fa-icon size="sm" :icon="['fas', 'times']" class="text-red" />
                </template>
              </template>

              <template v-slot:baseFee="{ row: aggregatedRow }">
                ${{ aggregatedRow.baseFee | formatNumber(2, 2) }}
              </template>

              <template v-slot:serviceFee="{ row: aggregatedRow }">
                ${{ aggregatedRow.serviceFee | formatNumber(2, 2) }}
              </template>

              <template v-slot:withholdingTax="{ row: aggregatedRow }">
                ${{ aggregatedRow.withholdingTax | formatNumber(2, 2) }}
              </template>

              <template v-slot:broadcastStartDate="{ row: aggregatedRow }">
                {{ aggregatedRow.broadcastStartDate }}
              </template>

              <template v-slot:broadcastEndDate="{ row: aggregatedRow }">
                {{ aggregatedRow.broadcastEndDate }}
              </template>

            </t-table>
            <b-row class="modal-footer float-right mt-4">
              <b-button class="btn-outline-red" @click="$bvModal.hide(`${row.recordingProprietaryId}-${row.payeeId}-${row.rightsHolderProprietaryId}-${row.distributionId}`)">
                {{ $t("ok") | capitalize }}
              </b-button>
            </b-row>
          </b-modal>
        </template>
      </t-table>
      <div v-if="currentTab === 'payouts_tab'">
        <ListPayoutsPage
          :isSubTab="true"
          :isTransactionsSubTab="isSubTab"
          :current-tab.sync="currentTab"
          :transactions-options="transactionsOptions"
          :distributionId="distributionId"
          :recordingResoundId="recordingResoundId"
          :rightsholderResoundId="rightsholderResoundId"
          @applyPayoutFilter="applyPayoutFilter"
        />
      </div>
      <modal
          id="refresh-modal"
          :customTitle="$t('refresh_request') | capitalize"
          modal-class="custom-modal info"
          modalType="edit"
          size="lg"
          @ok="transactionRefreshRequest"
          centered
          okTitle="Confirm"
          @close="cancelRefresh"
        >
          {{ refreshMessage() }}
      </modal>
      <modal
        id="removal-modal"
        modal-type="edit"
        centered
        size="lg"
        hide-footer
        noCloseOnBackdrop
        noCloseOnEsc
        @close="cancelRemove"
        :custom-title="$t('remove_transaction') | capitalize"
      >
        {{ removalMessage() }}
        <b-form @submit.prevent="transactionRemovalRequest">
          <b-form-group
            class="mt-4"
            :label="$t('reason') | capitalize"
            label-class="pb-0"
            label-cols="2"
          >
            <b-form-select
              v-model="removeTransaction.reason"
              :options="removalOptions"
            />
          </b-form-group>
          <b-form-group
            label-cols="2"
          >
            <b-form-textarea
              size="lg"
              class="mb-4 mt-4"
              debounce="500"
              name="comment"
              :placeholder="$t('make_comment') | capitalize"
              v-model="removeTransaction.comment"
              rows="5"
            />
          </b-form-group>
          <b-row class="modal-footer padding">
            <b-button @click="cancelRemove" class="btn-outline-dark">
              {{ $t("cancel") | capitalize }}
            </b-button>
            <b-button type="submit" class="btn-outline-red"
              :disabled="!(removeTransaction.comment && removeTransaction.reason)"
            >
              {{ $t("confirm") | capitalize }}
            </b-button>
          </b-row>
        </b-form>
      </modal>

    </template>
    <slot v-if="payoutsTransactions" name="footerButtons" />
  </list-layout>
</template>

<script>
import { capitalize, snakeCase } from "lodash"
import {
  distributionCollegeTypes,
  filterTypes,
  linkTypes,
  perPageItemsMd,
  transactionAggregations,
  transactionPerformerCategories,
  transactionsMaxExportCount,
  userPreferenceNames
} from "@/constants"
import { mapActions, mapState } from "vuex"
import DatabaseLink from "@/components/DatabaseLink.vue"
import FilterBuilder from "@/components/FilterBuilder.vue"
import ListPayoutsPage from "@/pages/Payouts/ListPayoutsPage"
import { Pages } from "@/utils/pages"
import Pagination from "@/components/Pagination.vue"
import TransactionsExportForm from "./TransactionsExportForm.vue"
import { listRouteMixin } from "@/utils/mixins"

import parseDate from "@/utils/date-parser"

const aggregationFilters = {
  "none": [
    "search",
    "rightsHolderProprietaryId",
    "payeeId",
    "payeeName",
    "societyName",
    "type",
    "performerCategory",
    "distribution",
    "distributionTariffCode",
    "tariffPeriodYear",
    "recordingProprietaryId",
    "artist",
    "isrc",
    "identifiers",
    "totalPlaycount",
    "vpp",
    "totalAmount",
    "baseFee",
    "serviceFee",
    "withholdingTax",
    "payableAmount",
    "calculationTimestamp",
    "eligibility",
    "lineupConflict",
    "mandateConflict",
    "genreConflict",
    "hold",
    "missingMandate",
    "hasFeaturedCount",
    "hasNonFeaturedCount",
    "preview",
    "payable",
    "pendingMakerMatching",
    "pendingPerformerMatching",
    "ownershipConflict",
    "moratorium",
    "share",
    "abovePayoutThreshold",
    "transactionTransactionBatchId",
    "transactionPayoutCompletedId",
  ],
  "rightsHolder": [
    "search",
    "rightsHolderProprietaryId",
    "payeeId",
    "payeeName",
    "totalAmount",
    "baseFee",
    "serviceFee",
    "withholdingTax",
    "payableAmount",
    "societyName",
    "abovePayoutThreshold",
    "transactionTransactionBatchId",
    "transactionPayoutCompletedId",
  ],
  "payeeTariff": [
    "search",
    "payeeId",
    "payeeName",
    "distribution",
    "distributionTariffCode",
    "tariffPeriodYear",
    "totalAmount",
    "baseFee",
    "serviceFee",
    "withholdingTax",
    "payableAmount",
    "abovePayoutThreshold",
    "transactionTransactionBatchId",
    "transactionPayoutCompletedId",
  ],
  "rightsHolderTariffPeriod": [
    "search",
    "distribution",
    "distributionTariffCode",
    "tariffPeriodYear",
    "rightsHolderProprietaryId",
    "payeeId",
    "payeeName",
    "societyName",
    "totalAmount",
    "baseFee",
    "serviceFee",
    "withholdingTax",
    "payableAmount",
    "abovePayoutThreshold",
    "transactionTransactionBatchId",
    "transactionPayoutCompletedId",
  ],
  "rightsHolderTariffRecording": [
    "search",
    "distribution",
    "distributionTariffCode",
    "tariffPeriodYear",
    "recordingProprietaryId",
    "artist",
    "rightsHolderProprietaryId",
    "societyName",
    "totalAmount",
    "baseFee",
    "serviceFee",
    "withholdingTax",
    "payableAmount",
    "share",
    "totalPlaycount",
    "type",
    "performerCategory",
    "abovePayoutThreshold",
    "transactionTransactionBatchId",
    "transactionPayoutCompletedId",
  ],
}


function toSortParam (param) {
  return snakeCase(param)
}

export default {
  name: "TransactionsList",
  mixins: [listRouteMixin],
  components: { TransactionsExportForm, DatabaseLink, Pagination, FilterBuilder, ListPayoutsPage },
  props: {
    recordingResoundId: {
      type: String,
      required: false,
    },
    rightsholderResoundId: {
      type: String,
      required: false,
    },
    distributionId: {
      type: String,
      required: false,
    },
    distributionValue: {
      type: String,
      required: false,
    },
    payoutData: {
      type: Object,
      required: false,
    },
    payoutsTransactions: {
      type: Boolean,
      default: false,
    },
    isSubTab: {
      type: Boolean,
      required: false,
    }
  },
  computed: {
    ...mapState("consts", ["memberCollectives", "tariffs"]),
    ...mapState("user", ["permissions"]),
    canExport () {
      return this.count <= transactionsMaxExportCount
    },
    actualPayoutData () {
      return this.payoutData || this.filteredPayoutData
    },
    activeFilters () {
      // hide filters not available for selected aggregation
      const aggregationKey = this.transactionsTable.selectedAggregation
      Object.keys(this.filters).forEach(key => {
        if (key === "recordingProprietaryId" && this.recordingResoundId) {
          this.filters[key].hidden = true
        } else if (key === "rightsHolderProprietaryId" && this.rightsholderResoundId) {
          this.filters[key].hidden = true
        } else if (key === "distribution" && this.distributionId) {
          this.filters[key].hidden = true
        } else if (key === "transactionTransactionBatchId") {
          this.filters.transactionTransactionBatchId.disable = !!this.isSubTab
        } else if (key === "transactionPayoutCompletedId") {
          this.filters.transactionPayoutCompletedId.disable = !!this.isSubTab
          if (this.isSubTab && this.payoutData?.payoutId) {
            this.filters.abovePayoutThreshold.disable = true
            this.filters.payable.disable = true
          }
        } else {
          this.filters[key].hidden = !aggregationFilters[aggregationKey].includes(key)
        }
      })
      return Object.fromEntries(Object.entries(this.filters))
    },
    filterBuilderId () {
      let id = userPreferenceNames.TRANSACTIONS_LIST_FILTER
      if (this.distributionId) {
        id = id + "_distribution"
      } else if (this.rightsholderResoundId) {
        id = id + "_rights_holder"
      } else if (this.recordingResoundId) {
        id = id + "_recording"
      } else if (this.payoutData?.id) {
        id = id + "_payout"
      }
      return id
    },
    transactionTableId () {
      return `transactions-${this.transactionsTable.selectedAggregation}`
    },
    transactionColumns () {
      let cols = []
      switch (this.transactionsTable.selectedAggregation) {
      case "rightsHolder":
        cols = [
          "rightsHolderProprietaryId",
          "name",
          "payeeName",
          "societyName",
          "totalAmount",
          "baseFee",
          "serviceFee",
          "withholdingTax",
          "payableAmount",
          "transactionsCount",
          "removeTransaction",
          "refreshTransaction",
        ]
        break
      case "payeeTariff":
        cols = [
          "payeeId",
          "payeeName",
          "distributionId",
          "totalAmount",
          "baseFee",
          "serviceFee",
          "withholdingTax",
          "payableAmount",
          "transactionsCount",
        ]
        break
      case "rightsHolderTariffPeriod":
        cols = [
          "distributionId",
          "payeeId",
          "rightsHolderProprietaryId",
          "name",
          "payeeName",
          "societyName",
          "totalAmount",
          "baseFee",
          "serviceFee",
          "withholdingTax",
          "payableAmount",
          "transactionsCount",
        ]
        break
      case "rightsHolderTariffRecording":
        cols = [
          "distributionId",
          "rightsHolderProprietaryId",
          "recordingProprietaryId",
          "title",
          "artist",
          "type",
          "performerCategory",
          "name",
          "societyName",
          "totalPlaycount",
          "share",
          "totalAmount",
          "baseFee",
          "serviceFee",
          "withholdingTax",
          "payableAmount",
          "calculationTimestamp",
          "transactionsCount",
          "drillDown",
          "removeTransaction",
          "refreshTransaction",
        ]
        break
      default:
        cols = [
          "payable",
          "preview",
          "rightsHolderProprietaryId",
          "name",
          "payeeId",
          "payeeName",
          "societyName",
          "type",
          "performerCategory",
          "recordingProprietaryId",
          "distributionId",
          "title",
          "artist",
          "isrc",
          "localProprietaryIds",
          "totalPlaycount",
          "vpp",
          "totalAmount",
          "baseFee",
          "serviceFee",
          "withholdingTax",
          "payableAmount",
          "share",
          "calculationTimestamp",
          "eligibility",
          "pendingMakerMatching",
          "pendingPerformerMatching",
          "lineupConflict",
          "mandateConflict",
          "genreConflict",
          "ownershipConflict",
          "missingMandate",
          "hasFeaturedCount",
          "hasNonFeaturedCount",
          "payoutId",
          "hold",
          "moratorium",
          "transactionId",
          "drillDown",
        ]
      }
      return cols.filter((col) => this.filters.transactionTransactionBatchId.value || !["removeTransaction", "refreshTransaction"].includes(col))
    },
    transactionTableOptions () {
      let options = {
        allSelected: false,
        sortable: [
          "distributionId",
          "payeeId",
          "rightsHolderProprietaryId",
          "recordingProprietaryId",
          "name",
          "payeeName",
          "societyName",
          "type",
          "performerCategory",
          "title",
          "artist",
          "isrc",
          "transactionId",
          "hold",
          "preview",
          "payable",
          "mandateConflict",
          "hasFeaturedCount",
          "hasNonFeaturedCount",
          "ownershipConflict",
          "eligibility",
          "lineupConflict",
          "moratorium",
          "genreConflict",
          "pendingPerformerMatching",
          "pendingMakerMatching",
          "totalAmount",
          "payableAmount",
          "totalPlaycount",
          "share",
          "payeeShare",
          "vpp",
          "deltaTotalAmount",
          "deltaPayableAmount",
          "deltaPlaycount",
          "calculationTimestamp",
          "deltaShare",
          "transactionsCount",
          "missingMandate",
          "payoutId",
          "baseFee",
          "serviceFee",
          "withholdingTax",
        ],
        headings: {
          "rightsHolderProprietaryId": this.$t("rights_holder_id"),
          "name": this.$t("rights_holder_name"),
          "payeeId": this.$t("payee_id"),
          "payeeName": this.$t("payee_name"),
          "societyName": this.$t("collective_society"),
          "type": capitalize(this.$t("type")),
          "performerCategory": capitalize(this.$t("category")),
          "recordingProprietaryId": this.$t("recording_id"),
          "transactionId": this.$t("transaction_id"),
          "title": capitalize(this.$t("recording_title")),
          "distributionId": this.$t("tariff_period_title"),
          "artist": capitalize(this.$t("artist")),
          "isrc": this.$t("isrc"),
          "localProprietaryIds": this.$t("proprietary_ids"),
          "hold": this.$t("no_hold"),
          "preview": capitalize(this.$t("preview")),
          "payable": capitalize(this.$t("payable")),
          "mandateConflict": capitalize(this.$t("no_mandate_conflict")),
          "ownershipConflict": this.$t("no_ownership_conflict"),
          "eligibility": capitalize(this.$t("eligible")),
          "lineupConflict": this.$t("no_lineup_conflict_title"),
          "moratorium": capitalize(this.$t("moratorium")),
          "genreConflict": this.$t("no_genre_conflict"),
          "pendingPerformerMatching": this.$t("matched_performer"),
          "pendingMakerMatching": this.$t("matched_maker"),
          "totalAmount": capitalize(this.$t("amount_gross")),
          "payableAmount": capitalize(this.$t("net_amount")),
          "totalPlaycount": this.$t("total_playcount"),
          "share": capitalize(this.$t("share")),
          "payeeShare": this.$t("share_payee"),
          "vpp": this.$t("vpp"),
          "deltaTotalAmount": this.$t("amount_total_delta"),
          "deltaPayableAmount": this.$t("amount_net_delta"),
          "deltaPlaycount": this.$t("total_playcount_delta"),
          "calculationTimestamp": this.$t("transaction_calculation_timestamp"),
          "deltaShare": this.$t("share_delta"),
          "transactionsCount": this.$t("transactions_count"),
          "missingMandate": capitalize(this.$t("mandate")),
          "hasFeaturedCount": capitalize(this.$t("featured_count")),
          "hasNonFeaturedCount": this.$t("non_featured_count"),
          "payoutId": capitalize(this.$t("payout")),
          "baseFee": capitalize(this.$t("base_fees")),
          "serviceFee": capitalize(this.$t("service_fees")),
          "withholdingTax": capitalize(this.$t("withholding_tax")),
          "drillDown": capitalize(this.$t("drill_down")),
          "removeTransaction": "",
          "refreshTransaction": "",
        },
        width: {
          "removeTransaction": "100",
          "refreshTransaction": "100",
        },
        columnsClasses: {},
        responseAdapter ({ data }) {
          return {
            data: data.results,
            count: data.count,
          }
        },
        requestFunction (queryParams) {
          queryParams = { ...queryParams, ...this.$route.query }
          // Need to add the distribution period filters explicitly
          if (Array.isArray(queryParams.tariff_period_year)) {
            queryParams.tariff_period_year.forEach((year, index) => {
              const yearNumber  = Number(year)
              if (!isNaN(yearNumber) && index === 0) {
                queryParams.distribution_period_start__gte = `${yearNumber}-01-01`
              } else {
                queryParams.distribution_period_end__lt = `${yearNumber + 1}-01-01`
              }
            })
          }
          queryParams.aggregate_by = transactionAggregations[queryParams.aggregate_by] || transactionAggregations.none

          // replace transactions specific payout filters with actual filter queries
          if (queryParams.transaction_transaction_batch_id) {
            queryParams.transaction_batch_id = queryParams.transaction_transaction_batch_id
            delete queryParams.transaction_transaction_batch_id
          }
          if (queryParams.transaction_payout_completed_id) {
            queryParams.payout_completed_id = queryParams.transaction_payout_completed_id
            delete queryParams.transaction_payout_completed_id
          }

          return this.$api.transactions.transactionsList(queryParams)
        },
      }
      if (this.transactionsTable.selectedAggregation === "none") {
        // a lot of columns in this case, need to hardcode default width for it to be readable
        options.width = {
          "removeTransaction": "100",
          "refreshTransaction": "100",
          "transactionId": "120",
          "distributionId": "140",
          "recordingProprietaryId": "80",
          "rightsHolderProprietaryId": "120",
          "title": "100",
          "artist": "100",
          "isrc": "100",
          "localProprietaryIds": "100",
          "name": "140",
          "payeeId": "100",
          "societyName": "120",
          "hold": "70",
          "preview": "60",
          "payable": "60",
          "mandateConflict": "60",
          "ownershipConflict": "60",
          "eligibility": "60",
          "lineupConflict": "60",
          "genreConflict": "60",
          "pendingPerformerMatching": "80",
          "pendingMakerMatching": "80",
          "totalAmount": "80",
          "payableAmount": "80",
          "totalPlaycount": "80",
          "type": "80",
          "performerCategory": "80",
          "share": "80",
          "payeeShare": "80",
          "payeeName": "80",
          "vpp": "80",
          "deltaTotalAmount": "80",
          "deltaPayableAmount": "80",
          "deltaPlaycount": "80",
          "calculationTimestamp": "120",
          "deltaShare": "80",
          "transactionsCount": "110",
          "payoutId": "80",
          "baseFee": "80",
          "serviceFee": "80",
          "withholdingTax": "80",
        }
      }
      return options
    },
    transactionsOptions () {
      return [
        {
          value: "transactions_tab",
          text: capitalize(this.$t("transactions")),
        },
        {
          value: "payouts_tab",
          text: capitalize(this.$t("payouts")),
          disabled: this.disablePayoutsTab,
        },
      ]
    },
  },
  methods: {
    ...mapActions("alert", ["success", "error"]),
    ...mapActions("consts", ["getTariffs"]),
    ...mapActions("alert", ["success", "error"]),
    parseDate,
    getProprietaryIds (identifiers) {
      let result = []
      identifiers.filter(id => id.type !== "isrc").forEach(
        identifiers => {
          identifiers.codes.forEach(code => {
            result.push({
              type: identifiers.type,
              code: code,
            })
          })
        }
      )
      return result
    },
    showModal (identifier) {
      this.$refs[identifier].show()
    },
    onTabChange ({ index }) {
      this.currentTab = this.tabs[index].ref
    },
    getData () {
      this.$refs.transactions?.getData()
    },
    onLoaded ({ data }) {
      this.count = data?.count || 0
      this.loading = false
    },
    exportData () {
      this.$bvModal.show("transactions-export-modal")
    },
    onFilter (name, value) {
      if (name === "distribution") {
        if (value === null) {
          this.changeFilter(name, value)
          this.changeFilter("distribution_id", value)
        } else {
          this.changeFilter(name, value.value)
          this.changeFilter("distribution_id", value.id)
        }
      } else if (name === "search") {
        if (value === "") {
          this.changeFilter(name, null)
        } else {
          this.changeFilter(name, value)
        }
      } else if (name === "rightsHolderProprietaryId") {
        if (value === null) {
          this.changeFilter(name, value)
        } else {
          this.changeFilter(name, value.resoundId)
        }
      } else if (name === "recordingProprietaryId") {
        if (value === null) {
          this.changeFilter(name, value)
        } else {
          this.changeFilter(name, value.resoundId)
        }
      } else if (["calculationTimestamp"].includes(name)) {
        const range = (value) ?
          [
            this.parseDate(value[0], this.$config.ISO_DATE_FORMAT),
            this.parseDate(value[1], this.$config.ISO_DATE_FORMAT),
          ] : []
        if (range.filter(r => r).length) {
          this.changeFilter("userTimezone", Intl.DateTimeFormat().resolvedOptions().timeZone)
          this.changeFilter(name, range)
        } else {
          this.changeFilter("userTimezone", null)
          this.changeFilter(name, null)
        }
      }
    },
    onSorted (data) {
      this.queryParams = {
        ...this.queryParams,
        ["sorting"]: data.map(item => item.ascending ? toSortParam(item.column) : `-${toSortParam(item.column)}`)
      }
      this.applyFilters()
    },
    onFiltersApply () {
      if (!this.firstLoaded && this.filtersPending) {
        this.firstLoaded = true
      }
      this.applyFilters()
      // This is to fix the problem on general transaction view where we don't have payoutData set
      const batchId = this.filters.transactionTransactionBatchId.value
      if ( batchId && !this.actualPayoutData?.transactionBatchId !== batchId) {
        this.$api.payouts.payoutsList({ transaction_batch_id: batchId }).then(response => {
          this.filteredPayoutData = response?.data?.results?.[0]
        })
      } else {
        this.filteredPayoutData = null
      }
    },
    loadAllTransactions () {
      this.firstLoaded = true
    },
    handleRemoveTransaction (row) {
      this.currentTransaction = row
      this.$bvModal.show("removal-modal")
    },
    handleRefreshTransaction (row) {
      this.currentTransaction = row
      this.$bvModal.show("refresh-modal")
    },
    cancelRefresh () {
      this.currentTransaction = null
      this.$bvModal.hide("refresh-modal")
    },
    cancelRemove () {
      this.currentTransaction = null
      this.$bvModal.hide("removal-modal")
      this.removeTransaction.comment = ""
      this.removeTransaction.reason = null
    },
    transactionRefreshRequest () {
      let data = {
        aggregationLevel: this.getAggregationForRemoval(this.transactionsTable.selectedAggregation),
        payoutId: this.actualPayoutData.id,
        distributionId: this.currentTransaction.distributionId,
        societyName: this.currentTransaction.societyName,
        distributionDescription: this.currentTransaction.distributionDescription,
        payeeId: this.currentTransaction.payeeId,
        payeeName: this.currentTransaction.payeeName,
        playcount: this.currentTransaction.deltaPlaycount,
        amount: this.currentTransaction.deltaTotalAmount,
        share: this.currentTransaction.share,
        tariff: { code: this.currentTransaction?.distributionTariffCode },
        transactionsCount: this.currentTransaction.transactionsCount,
        distributionStartDate: this.currentTransaction?.distributionPeriodStart,
        distributionEndDate: this.currentTransaction?.distributionPeriodStart,
      }

      if (this.currentTransaction.recordingProprietaryId ){
        data.recording = { resoundId: this.currentTransaction.recordingProprietaryId }
      }

      if (this.currentTransaction.rightsHolderProprietaryId ) {
        data.rightsHolder = { resoundId: this.currentTransaction.rightsHolderProprietaryId }
      }
      this.$api.payouts.createTransactionRefreshRequest(data).then(() => {
        this.success(capitalize(this.$t("refresh_request_success")))
        this.currentTransaction = null
        this.$bvModal.hide("refresh-modal")
        this.getData()
      }).catch(error => {
        const { status } = error.response
        if (status === 400) {
          this.error(capitalize(this.$t("refresh_request_fail")))
        }
      })
    },
    transactionRemovalRequest () {
      let data = {
        aggregationLevel: this.getAggregationForRemoval(this.transactionsTable.selectedAggregation),
        payoutId: this.actualPayoutData.id,
        comment: this.removeTransaction.comment,
        reason: this.removeTransaction.reason,
        payeeId: this.currentTransaction.payeeId,
        payeeName: this.currentTransaction.payeeName,
        distributionId: this.currentTransaction.distributionId,
        distributionDescription: this.currentTransaction.distributionDescription,
        societyName: this.currentTransaction.societyName,
        playcount: this.currentTransaction.deltaPlaycount,
        amount: this.currentTransaction.deltaTotalAmount,
        share: this.currentTransaction.share,
        transactionsCount: this.currentTransaction.transactionsCount,
        distributionStartDate: this.currentTransaction?.distributionPeriodStart,
        distributionEndDate: this.currentTransaction?.distributionPeriodStart,
      }

      if (this.currentTransaction.recordingProprietaryId ){
        data.recording = { resoundId: this.currentTransaction.recordingProprietaryId }
      }

      if (this.currentTransaction.rightsHolderProprietaryId ) {
        data.rightsHolder = { resoundId: this.currentTransaction.rightsHolderProprietaryId }
      }

      this.$api.payouts.createTransactionRemovalRequest(data).then(() => {
        this.success(capitalize(this.$t("removal_request_success")))
        this.$bvModal.hide("removal-modal")
        this.getData()
      }).catch(error => {
        const { status } = error.response
        if (status === 400) {
          this.error(capitalize(this.$t("removal_request_fail")))
        }
      })
      this.removeTransaction.comment = ""
      this.removeTransaction.reason = null
    },
    getAggregationForRemoval (value) {
      if (value === "rightsHolder") {
        return "rights_holder"
      } else if (value === "rightsHolderTariffRecording") {
        return "recording_rights_holder_distribution"
      }
    },
    removalMessage () {
      return `You are about to submit a request to remove ${this.currentTransaction?.rights_holder_proprietary_id} for ${this.currentTransaction?.name}, with ${this.currentTransaction?.transactionsCount} transactions and ${this.currentTransaction?.deltaTotalAmount} gross amount. Please select a reason for your request from the list to proceed.`
    },
    refreshMessage () {
      return `You are about to submit a transactions refresh request for ${this.currentTransaction?.name}`
    },
    applyPayoutFilter (filterName, id) {
      this.$nextTick(() => {
        this.$refs.transactionFilters.$nextTick(() => {
          this.$refs.transactionFilters.addFilter(filterName, id)
          if (filterName === "transactionPayoutCompletedId") {
            this.$refs.transactionFilters.removeFilter("transactionTransactionBatchId", false)
          } else if (filterName === "transactionTransactionBatchId") {
            this.$refs.transactionFilters.removeFilter("transactionPayoutCompletedId", false)
          }
          this.$refs.transactionFilters.addFilter("payable", "1")
          this.$refs.transactionFilters.addFilter("abovePayoutThreshold", "1")
        })
      })

      if (!this.isSubTab) {
        this.$refs.transactionsTabs.tabIndex = this.tabs.findIndex(tab => tab.ref === "transactions_tab")
        this.firstLoaded = true
      }
    }
  },
  watch: {
    "transactionsTable.selectedAggregation": function (value) {
      delete this.queryParams["sorting"]
      this.queryParams = {
        ...this.queryParams, "aggregate_by": value
      }
      this.changeFilter("aggregateBy", value, true)
    },
    // In case if page is mounted before memberCollectives and tariffs loaded to store
    "$store.state.consts.memberCollectives": function (value) {
      this.filters.societyName.options = value.map(x => ({
        text: x.name,
        value: x.name,
      }))
    },
    "$store.state.consts.tariffs": function (value) {
      this.filters.distributionTariffCode.options = value.map(t => ({
        text: `${t.code} - ${t.text}`, value: t.code
      }))
    }
  },
  data () {
    return {
      filteredPayoutData: null,
      firstLoaded: false,
      count: 0,
      page: 1,
      pageNumber: 1,
      limit: perPageItemsMd,
      loading: true,
      queryParams: {},
      useGtLtFilters: true,
      linkTypes,
      Pages,
      transactionPerformerCategories,
      disablePayoutsTab: false,
      transactionsTable: {
        aggregationOptions: [
          {
            value: "none",
            text: capitalize(this.$t("none")),
          },
          {
            value: "payeeTariff",
            text: this.$t("payee_tariff"),
          },
          {
            value: "rightsHolder",
            text: this.$t("rights_holder_title"),
          },
          {
            value: "rightsHolderTariffPeriod",
            text: this.$t("rights_holder_tariff_period"),
          },
          {
            value: "rightsHolderTariffRecording",
            text: this.$t("rights_holder_tariff_recording"),
          },
        ],
        selectedAggregation: "none",
      },
      removalOptions: [
        {
          value: "unidentified_mandate_conflict",
          text: this.$t("unidentified_mandate_conflict"),
        },
        {
          value: "loss_of_mandate",
          text: this.$t("loss_of_mandate"),
        },
        {
          value: "unidentified_conflict",
          text: this.$t("unidentified_conflict"),
        },
        {
          value: "general_rejection",
          text: this.$t("general_rejection"),
        },
      ],
      removeTransaction: {
        reason: null,
        comment: ""
      },
      filters: {
        search: { value: "", defaultValue: "", type: filterTypes.TEXT, label: capitalize(this.$t("search")), noWatch: true },
        userTimezone: { value: null, defaultValue: null, hidden: true },
        payeeId: {
          value: null,
          defaultValue: null,
          type: filterTypes.TEXT,
          label: this.$t("payee_id"),
          hidden: true,
        },
        payeeName: {
          value: null,
          defaultValue: null,
          type: filterTypes.TEXT,
          label: this.$t("payee_name"),
          hidden: true,
        },
        distributionTariffCode: {
          value: null,
          defaultValue: null,
          label: capitalize(this.$t("tariff")),
          options: [],
          valueField: "distributionTariffCode",
          emptyLabel: capitalize(this.$t("all")),
          type: filterTypes.SELECT,
        },
        tariffPeriodYear: {
          value: null,
          defaultValue: null,
          placeholder: `${capitalize(this.$t("tariff_period_title"))} ${this.$t("year")}`,
          type: filterTypes.YEAR_RANGE,
          label: `${capitalize(this.$t("tariff_period_title"))} ${this.$t("year")}`,
        },
        distribution: {
          value: null,
          defaultValue: null,
          excludeName: "description",
          placeholder: capitalize(this.$t("search_tariff_period")),
          type: filterTypes.SELECT_SEARCH,
          label: capitalize(this.$t("tariff_period_title")),
          valueField: "description",
          textField: "description",
          searchName: "description",
          listEndpoint: this.$api.distributions.distributionsList,
          noWatch: true,
        },
        rightsHolderProprietaryId: {
          value: null,
          defaultValue: null,
          type: filterTypes.SELECT_SEARCH,
          label: capitalize(this.$t("rights_holder")),
          placeholder: capitalize(this.$t("search_rightsholder")),
          excludeName: "id",
          valueField: "resoundId",
          textField: "name",
          searchName: "search",
          listEndpoint: this.$api.rightsholders.rightsholdersList,
          noWatch: true,
        },
        recordingProprietaryId: {
          value: null,
          defaultValue: null,
          type: filterTypes.SELECT_SEARCH,
          label: capitalize(this.$t("recording")),
          placeholder: capitalize(this.$t("search_recording")),
          excludeName: "id",
          valueField: "resoundId",
          textFunction: (r => `${r.artist} - ${r.title}`),
          searchName: "fuzzy_search",
          listEndpoint: this.$api.repertoire.recordingsList,
          noWatch: true,
        },
        societyName: {
          value: null,
          defaultValue: null,
          options: [],
          emptyLabel: capitalize(this.$t("all")),
          type: filterTypes.SELECT,
          label: this.$t("collective_society"),
          hidden: true,
        },
        type: {
          value: null,
          defaultValue: null,
          options: Object.values(distributionCollegeTypes).map(v => ({ value: v, text: capitalize(this.$t(v)) })),
          emptyLabel: capitalize(this.$t("all")),
          type: filterTypes.SELECT,
          label: capitalize(this.$t("type")),
          hidden: true,
        },
        performerCategory: {
          value: null,
          defaultValue: null,
          options: Object.entries(transactionPerformerCategories).map(([k, v]) => ({ value: k, text: capitalize(this.$t(v)) })),
          emptyValue: capitalize(this.$t("all")),
          type: filterTypes.SELECT,
          label: `${capitalize(this.$t("performer"))} ${this.$t("category")}`,
          hidden: true,
        },
        artist: {
          value: null,
          defaultValue: null,
          type: filterTypes.TEXT,
          label: capitalize(this.$t("artist")),
          hidden: true,
        },
        isrc: {
          value: null,
          defaultValue: null,
          type: filterTypes.TEXT,
          label: this.$t("isrc"),
          hidden: true,
        },
        totalPlaycount: {
          value: null,
          defaultValue: null,
          label: capitalize(this.$t("playcount")),
          type: filterTypes.NUMBER_RANGE_NO_SLIDER,
          singleBound: false,
          hidden: true,
        },
        vpp: {
          value: null,
          defaultValue: null,
          label: capitalize(this.$t("vpp")),
          type: filterTypes.NUMBER_RANGE_NO_SLIDER,
          singleBound: false,
          hidden: true,
        },
        totalAmount: {
          value: null,
          defaultValue: null,
          label: capitalize(this.$t("amount_gross")),
          type: filterTypes.NUMBER_RANGE_NO_SLIDER,
          singleBound: false,
          hidden: true,
        },
        baseFee: {
          value: null,
          defaultValue: null,
          label: capitalize(this.$t("base_fees")),
          type: filterTypes.NUMBER_RANGE_NO_SLIDER,
          singleBound: false,
          hidden: true,
        },
        serviceFee: {
          value: null,
          defaultValue: null,
          label: capitalize(this.$t("service_fees")),
          type: filterTypes.NUMBER_RANGE_NO_SLIDER,
          singleBound: false,
          hidden: true,
        },
        withholdingTax: {
          value: null,
          defaultValue: null,
          label: capitalize(this.$t("withholding_tax")),
          type: filterTypes.NUMBER_RANGE_NO_SLIDER,
          singleBound: false,
          hidden: true,
        },
        calculationTimestamp: {
          value: null,
          defaultValue: null,
          type: filterTypes.DATE_RANGE,
          format: "YYYY-MM-DD",
          label: capitalize(this.$t("date_start")),
          noWatch: true,
          hidden: true,
        },
        eligibility: {
          value: null,
          defaultValue: null,
          options: [
            { value: "ineligible", text: "Ineligible" },
            { value: "eligible", text: "Eligible" },
          ],
          emptyLabel: capitalize(this.$t("all")),
          type: filterTypes.SELECT,
          label: capitalize(this.$t("eligible")),
          hidden: true,
        },
        payable: {
          value: this.payoutData?.id ? "1" : null,
          defaultValue: null,
          options: [
            { value: "0", text: capitalize(this.$t("no")) },
            { value: "1", text: capitalize(this.$t("yes")) },
          ],
          emptyLabel: capitalize(this.$t("all")),
          emptyValue: null,
          type: filterTypes.SELECT,
          dataType: String,
          label: capitalize(this.$t("payable")),
        },
        abovePayoutThreshold: {
          value: this.payoutData?.id ? "1" : null,
          defaultValue: null,
          options: [
            { value: "0", text: capitalize(this.$t("no")) },
            { value: "1", text: capitalize(this.$t("yes")) },
          ],
          emptyLabel: capitalize(this.$t("all")),
          emptyValue: null,
          type: filterTypes.SELECT,
          dataType: String,
          label: capitalize(this.$t("above_threshold")),
        },
        lineupConflict: {
          value: null,
          defaultValue: null,
          options: [
            { value: "1", text: capitalize(this.$t("no")) },
            { value: "0", text: capitalize(this.$t("yes")) },
          ],
          emptyLabel: capitalize(this.$t("all")),
          type: filterTypes.SELECT,
          label: capitalize(this.$t("no_lineup_conflict_title")),
        },
        mandateConflict: {
          value: null,
          defaultValue: null,
          options: [
            { value: "1", text: capitalize(this.$t("no")) },
            { value: "0", text: capitalize(this.$t("yes")) },
          ],
          emptyLabel: capitalize(this.$t("all")),
          type: filterTypes.SELECT,
          label: capitalize(this.$t("no_mandate_conflict")),
        },
        hold: {
          value: null,
          defaultValue: null,
          options: [
            { value: "1", text: capitalize(this.$t("no")) },
            { value: "0", text: capitalize(this.$t("yes")) },
          ],
          emptyLabel: capitalize(this.$t("all")),
          type: filterTypes.SELECT,
          label: this.$t("no_hold"),
        },
        genreConflict: {
          value: null,
          defaultValue: null,
          options: [
            { value: "1", text: capitalize(this.$t("no")) },
            { value: "0", text: capitalize(this.$t("yes")) },
          ],
          emptyLabel: capitalize(this.$t("all")),
          type: filterTypes.SELECT,
          label: this.$t("no_genre_conflict"),
        },
        missingMandate: {
          value: null,
          defaultValue: null,
          options: [
            { value: "true", text: capitalize(this.$t("no")) },
            { value: "false", text: capitalize(this.$t("yes")) },
          ],
          emptyLabel: capitalize(this.$t("all")),
          type: filterTypes.SELECT,
          label: capitalize(this.$t("mandate")),
        },
        hasFeaturedCount: {
          value: null,
          defaultValue: null,
          options: [
            { value: "false", text: capitalize(this.$t("no")) },
            { value: "true", text: capitalize(this.$t("yes")) },
          ],
          emptyLabel: capitalize(this.$t("all")),
          type: filterTypes.SELECT,
          label: capitalize(this.$t("featured_count")),
        },
        moratorium: {
          value: null,
          defaultValue: null,
          options: [
            { value: "0", text: capitalize(this.$t("no")) },
            { value: "1", text: capitalize(this.$t("yes")) },
          ],
          emptyLabel: capitalize(this.$t("all")),
          type: filterTypes.SELECT,
          label: capitalize(this.$t("moratorium")),
        },
        hasNonFeaturedCount: {
          value: null,
          defaultValue: null,
          options: [
            { value: "false", text: capitalize(this.$t("no")) },
            { value: "true", text: capitalize(this.$t("yes")) },
          ],
          emptyLabel: capitalize(this.$t("all")),
          type: filterTypes.SELECT,
          label: this.$t("non_featured_count"),
        },
        pendingMakerMatching: {
          value: null,
          defaultValue: null,
          options: [
            { value: "1", text: capitalize(this.$t("no")) },
            { value: "0", text: capitalize(this.$t("yes")) },
          ],
          emptyLabel: capitalize(this.$t("all")),
          type: filterTypes.SELECT,
          label: this.$t("matched_maker"),
        },
        pendingPerformerMatching: {
          value: null,
          defaultValue: null,
          options: [
            { value: "1", text: capitalize(this.$t("no")) },
            { value: "0", text: capitalize(this.$t("yes")) },
          ],
          emptyLabel: capitalize(this.$t("all")),
          type: filterTypes.SELECT,
          label: this.$t("matched_performer"),
        },
        payableAmount: {
          value: null,
          defaultValue: null,
          type: filterTypes.NUMBER_RANGE_NO_SLIDER,
          label: capitalize(this.$t("net_amount")),
        },
        share: {
          value: null,
          defaultValue: null,
          type: filterTypes.NUMBER_RANGE_NO_SLIDER,
          label: capitalize(this.$t("share")),
        },
        ownershipConflict: {
          value: null,
          defaultValue: null,
          options: [
            { value: "1", text: capitalize(this.$t("no")) },
            { value: "0", text: capitalize(this.$t("yes")) },
          ],
          emptyLabel: capitalize(this.$t("all")),
          type: filterTypes.SELECT,
          label: capitalize(this.$t("no_ownership_conflict")),
        },
        transactionPayoutCompletedId: {
          value: this.payoutData?.payoutId || null,
          defaultValue: this.payoutData?.payoutId || null,
          disabled: !!this.payoutData?.payoutId,
          type: filterTypes.TEXT,
          label: this.$t("payout_ID"),
        },
        transactionTransactionBatchId: {
          value: !this.payoutData?.payoutId && this.payoutData?.transactionBatchId ? this.payoutData?.transactionBatchId : null,
          defaultValue: !this.payoutData?.payoutId && this.payoutData?.transactionBatchId ? this.payoutData?.transactionBatchId : null,
          disabled: !!this.payoutData?.transactionBatchId,
          type: filterTypes.TEXT,
          label: this.$t("batch_id"),
        },
      },
      preview: {
        value: null,
        defaultValue: null,
        options: [
          { value: "0", text: capitalize(this.$t("no")) },
          { value: "1", text: capitalize(this.$t("yes")) },
        ],
        emptyLabel: capitalize(this.$t("all")),
        type: filterTypes.SELECT,
        label: capitalize(this.$t("preview_flag")),
      },
      userPreferenceNames,
      currentTab: "transactions_tab",
      tabs: [
        {
          ref: "transactions_tab",
          title: capitalize(this.$t("transactions")),
        },
        {
          ref: "payouts_tab",
          title: capitalize(this.$t("payouts")),
        },
      ],
      localProprietaryIdsOptions: {
        sortable: [],
        headings: {
          id: capitalize(this.$t("id")),
          mc: capitalize(this.$t("mc")),
        }
      },
      localProprietaryIdsColumns: [
        "id",
        "mc",
      ],
      drillDownTableOptions: {
        sortable: [],
        headings: {
          totalAmount: capitalize(this.$t("amount_gross")),
          rawAmount: capitalize(this.$t("amount_raw")),
          payableAmount: capitalize(this.$t("net_amount")),
          totalPlaycount: capitalize(this.$t("total_playcount")),
          share: capitalize(this.$t("share")),
          name: capitalize(this.$t("rights_holder_name")),
          payable: capitalize(this.$t("payable")),
          baseFee: capitalize(this.$t("base_fees")),
          serviceFee: capitalize(this.$t("service_fees")),
          withholdingTax: capitalize(this.$t("withholding_tax")),
          broadcastStartDate: capitalize(this.$t("broadcast_start")),
          broadcastEndDate: capitalize(this.$t("broadcast_end")),
        }
      },
      drillDownTableColumns: [
        "payable",
        "name",
        "totalAmount",
        "rawAmount",
        "payableAmount",
        "totalPlaycount",
        "share",
        "broadcastStartDate",
        "broadcastEndDate",
        "baseFee",
        "serviceFee",
        "withholdingTax",
      ],
      currentTransaction: null
    }
  },
  mounted () {
    this.getTariffs()
    this.transactionsTable.selectedAggregation = this.$route.query["aggregate_by"] || "none"
    const filtersFromQuery = Object.keys(this.$route.query).filter(
      key => !["sorting", "aggregate_by", "page", "limit"].includes(key)
    )
    // In case when page is opened with some filters applied (through shared link for example)
    if (filtersFromQuery.length > 0) {
      this.firstLoaded = true
      filtersFromQuery.forEach(filter => {
        if (
          filter === "recordingProprietaryId" ||
          filter === "rightsHolderProprietaryId" ||
          filter === "distribution_id" ||
          filter === "distribution"
        ) {
          this.changeFilter(filter, this.$route.query[filter])
          if (filter === "distribution_id") {
            this.filters["distribution"].searchValue = this.$route.query[filter]
          } else {
            this.filters[filter].searchValue = this.$route.query[filter]
          }
        }
      })
    }
    // Set options when loaded
    this.filters.societyName.options = this.memberCollectives.map(x => ({
      text: x.name,
      value: x.name,
    }))

    if (this.isSubTab) {
      if (this.recordingResoundId) {
        this.changeFilter("recordingProprietaryId", this.recordingResoundId)
      } else if (this.rightsholderResoundId) {
        this.changeFilter("rightsHolderProprietaryId", this.rightsholderResoundId)
      } else if (this.distributionId) {
        this.changeFilter("distribution_id", this.distributionId)
        this.firstLoaded = true
      } else if (this.payoutData?.payoutId){
        this.changeFilter("transactionPayoutCompletedId", this.payoutData.payoutId)
        if (this.filters.abovePayoutThreshold.value) {
          this.changeFilter("abovePayoutThreshold", this.filters.abovePayoutThreshold.value)
        }
        if (this.filters.payable.value) {
          this.changeFilter("payable", this.filters.payable.value)
        }
        if (!this.firstLoaded) {
          this.$nextTick(() => {
            this.$refs.transactionFilters.addFilter("transactionPayoutCompletedId", this.payoutData.payoutId)
            this.$refs.transactionFilters.removeFilter("transactionTransactionBatchId", false)
            this.$refs.transactionFilters.addFilter("abovePayoutThreshold", "1")
            this.$refs.transactionFilters.addFilter("payable", "1")
          })
          this.firstLoaded = true
        }
      } else if (this.payoutData?.transactionBatchId) {
        this.changeFilter("transactionTransactionBatchId", this.payoutData.transactionBatchId)
        if (this.filters.abovePayoutThreshold.value) {
          this.changeFilter("abovePayoutThreshold", this.filters.abovePayoutThreshold.value)
        }
        if (this.filters.payable.value) {
          this.changeFilter("payable", this.filters.payable.value)
        }
        if (!this.firstLoaded) {
          this.$nextTick(() => {
            this.$refs.transactionFilters.addFilter("transactionTransactionBatchId", this.payoutData.transactionBatchId)
            this.$refs.transactionFilters.removeFilter("transactionPayoutCompletedId", false)
            this.$refs.transactionFilters.addFilter("abovePayoutThreshold", "1")
            this.$refs.transactionFilters.addFilter("payable", "1")
          })
          this.firstLoaded = true
        }
      }
    }
  },
}

</script>

<style lang="scss" scoped>

.no-filters-text {
  font-size: 1.2rem;
  width: 350px;
  margin-bottom: 2rem;
}

::v-deep .no-title {
  padding-top: 15px;
}

.no-filter-img {
  width: calc(100dvh - 380px);
  max-width: 750px;
  min-width: 360px;
}
</style>
